import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Button, Card, Chip, Grid, Stack, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { enqueueSnackbar } from "notistack";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { EmptyState } from "@/components/ui";
import { useApiQuery } from "../../hooks";
import type { TestAgentV2Session } from "../../types/test-agent-2";
import { clickableProps } from "../../utils/keyboard";
import { humanizeText } from "../../utils/text";
import { VideoPlayer } from "../video-player";
import type { IssueReport, TestCreatorDoneIntent } from "./details";
import { SessionDetailsPanel } from "./session-details-panel";

const INITIAL_LIMIT = 6;
const LOAD_MORE_INCREMENT = 6;

const getStatusColor = (status: string): "default" | "success" | "warning" | "error" | "info" => {
  switch (status) {
    case "closed":
      return "success";
    case "running":
      return "info";
    case "idle":
      return "warning";
    case "needs_user_input":
      return "warning";
    case "error":
      return "error";
    default:
      return "default";
  }
};

interface AgentCardProps {
  session: TestAgentV2Session;
  onClick: () => void;
}

const textForLlm = (issue: IssueReport) => {
  let text = `### Issue: ${issue.title}\n`;
  text += `${issue.description}\n\n`;
  if (issue.severity) {
    text += `- Severity: ${humanizeText(issue.severity)}\n`;
  }
  if (issue.issue_type) {
    text += `- Type: ${humanizeText(issue.issue_type)}\n`;
  }
  if (issue.url) {
    text += `- URL: ${issue.url}\n`;
  }
  if (issue.help) {
    text += `- Help: ${issue.help}\n`;
  }
  if (issue.recommendations && issue.recommendations.length > 0) {
    text += `- Recommendations:\n`;
    issue.recommendations.forEach((rec) => {
      text += `  - ${rec}\n`;
    });
  }
  return text;
};

const AgentCard = ({ session, onClick }: AgentCardProps) => {
  const task = session.data?.history?.[0]?.task || "No task description";
  const status = session.data?.status || "unknown";
  const createdAt = session.created_at
    ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true })
    : "";

  const isRunning = status === "running" || status === "idle";
  const isClosed = status === "closed";
  const liveViewUrl = session.data?.liveview_url;
  const recordingPath = session.data?.recording_path;

  // Build liveview URL with hidden controls
  const liveViewIframeUrl = useMemo(() => {
    if (!liveViewUrl) return null;
    const url = new URL(
      liveViewUrl.includes("liveview-2")
        ? liveViewUrl
        : liveViewUrl.replace("liveview", "liveview-2"),
    );
    url.searchParams.set("hideControls", "true");
    return url.toString();
  }, [liveViewUrl]);

  const lastDone = useMemo(() => {
    const maybeDone = session.data?.last_done;
    if (!maybeDone) return null;
    return maybeDone as TestCreatorDoneIntent;
  }, [session.data?.last_done]);

  const hasIssues = lastDone?.issues && lastDone.issues.length > 0;

  const handleCopyForLlm = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!lastDone || !hasIssues) return;

      let text = `## QA Session Report\n\n`;
      text += `**Status:** ${humanizeText(lastDone.status)}\n`;
      text += `**Summary:** ${lastDone.message}\n\n`;
      if (lastDone.reasoning) {
        text += `**Reasoning:** ${lastDone.reasoning}\n\n`;
      }
      text += `---\n\n`;
      text += `## Issues Found (${lastDone.issues.length})\n\n`;

      lastDone.issues.forEach((issue, index) => {
        text += `${index + 1}. `;
        text += textForLlm(issue);
        text += `\n`;
      });

      navigator.clipboard.writeText(text);
      enqueueSnackbar("All issues copied to clipboard", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    [lastDone, hasIssues],
  );

  const showPreview = (isRunning && liveViewIframeUrl) || (isClosed && recordingPath);

  // Memoize recording path to prevent re-renders when grid refetches

  const stableRecordingPath = useMemo(() => recordingPath, [recordingPath]);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "280px",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
      }}
      {...clickableProps(onClick)}
      onClick={onClick}
    >
      {/* Video/Liveview - Full width background */}
      {showPreview && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "common.black",
          }}
        >
          {isRunning && liveViewIframeUrl ? (
            <iframe
              src={liveViewIframeUrl}
              sandbox="allow-same-origin allow-scripts"
              style={{
                width: "100%",
                height: "300px",
                border: "none",
                pointerEvents: "none",
              }}
              title="Live View"
            />
          ) : isClosed && stableRecordingPath ? (
            <VideoPlayer
              key={session.id}
              src={stableRecordingPath}
              width="100%"
              height="300px"
              controls={false}
              autoPlay={true}
              muted={true}
              loop={true}
              playbackRate={8}
            />
          ) : null}
        </Box>
      )}

      {/* Gradient overlay for text readability */}
      {showPreview && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Content overlay */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          minHeight: showPreview ? 180 : "auto",
          p: 2,
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Chip label={humanizeText(status)} size="small" color={getStatusColor(status)} />
            <Typography
              variant="caption"
              sx={{
                color: showPreview ? "rgba(255,255,255,0.7)" : "text.secondary",
              }}
            >
              {createdAt}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              color: showPreview ? "common.white" : "text.primary",
              fontWeight: showPreview ? 500 : 400,
            }}
          >
            {task}
          </Typography>
        </Stack>
      </Box>

      {/* Copy for LLM button - shown when there are issues */}
      {hasIssues && (
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: 2,
            pb: 1.5,
          }}
          {...clickableProps((e: unknown) => {
            if (e && typeof e === "object" && "stopPropagation" in e) {
              (e as { stopPropagation: () => void }).stopPropagation();
            }
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant={showPreview ? "contained" : "outlined"}
            size="small"
            fullWidth
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyForLlm}
            sx={
              showPreview
                ? {
                    backgroundColor: "rgba(255,255,255,0.9)",
                    color: "common.black",
                    "&:hover": { backgroundColor: "common.white" },
                  }
                : {}
            }
          >
            Copy {lastDone.issues.length} issue
            {lastDone.issues.length !== 1 ? "s" : ""} for LLM
          </Button>
        </Box>
      )}
    </Card>
  );
};

export const AgentsTab = () => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_LIMIT);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const { data: sessions, isLoading } = useApiQuery<TestAgentV2Session[]>(
    "/vibe-qa/sessions?limit=100",
    {
      refetchInterval: 5000,
    },
  );

  const visibleSessions = sessions?.slice(0, visibleCount) ?? [];
  const hasMore = (sessions?.length ?? 0) > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + LOAD_MORE_INCREMENT);
  };

  const handleSessionClick = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setPanelOpen(true);
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
    setSelectedSessionId(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Loading agents...
        </Typography>
      </Box>
    );
  }

  if (!sessions || sessions.length === 0) {
    return <EmptyState message="No agent sessions found. Start a new test to create one!" />;
  }

  return (
    <>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {visibleSessions.map((session) => (
            <Grid key={session.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <AgentCard session={session} onClick={() => handleSessionClick(session.id)} />
            </Grid>
          ))}
        </Grid>

        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Load more ({(sessions?.length ?? 0) - visibleCount} remaining)
            </Button>
          </Box>
        )}
      </Stack>

      <SessionDetailsPanel
        sessionId={selectedSessionId}
        open={panelOpen}
        onClose={handleClosePanel}
      />
    </>
  );
};
